import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ApiClient from '../../../methods/api/apiClient';
import socketService from '../../../services/SocketService';

const TABS = [
  { key: 'all', label: 'All', icon: 'fa-inbox' },
  { key: 'bids', label: 'Bids', icon: 'fa-gavel' },
  { key: 'loads', label: 'Loads', icon: 'fa-truck' },
  { key: 'compliance', label: 'Compliance', icon: 'fa-shield' },
  { key: 'finance', label: 'Finance', icon: 'fa-dollar' },
  { key: 'account', label: 'Account', icon: 'fa-user-circle' },
  { key: 'system', label: 'System', icon: 'fa-bullhorn' },
];

const PRIORITY_COLORS = {
  urgent: '#dc2626',
  high: '#f59e0b',
  normal: '#3E549D',
  low: '#9ca3af',
};

const CAT_COLORS = {
  bids: '#8b5cf6',
  loads: '#3E549D',
  compliance: '#f59e0b',
  finance: '#10b981',
  account: '#06b6d4',
  system: '#6b7280',
};

const NotificationBell = () => {
  const user = useSelector(state => state.user);
  const history = useHistory();
  const [counts, setCounts] = useState({ total: 0, bids: 0, loads: 0, compliance: 0, finance: 0, account: 0, system: 0 });
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [bellPos, setBellPos] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef(null);
  const bellRef = useRef(null);

  const computeBellPos = useCallback(() => {
    if (bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect();
      setBellPos({
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
      });
    }
  }, []);

  const getSendTo = useCallback(() => {
    if (user?.role === 'staff' && user?.addedByDetails?.role === 'admin') {
      return `${user?.addedByDetails?.id || user?.addedByDetails?._id},${user?.id || user?._id}`;
    }
    return user?.id || user?._id;
  }, [user]);

  // Fetch unread counts per category for tab badges
  const fetchCategoryCounts = useCallback(() => {
    if (!user?.id) return;
    const sendTo = getSendTo();
    ApiClient.get('notification/category-counts', { send_to: sendTo }).then(res => {
      if (res?.success) {
        setCounts(res.data || { total: 0 });
      }
    });
  }, [user?.id, getSendTo]);

  // Fetch notifications for the active tab
  const fetchForTab = useCallback((tab) => {
    if (!user?.id) return;
    setLoading(true);
    const sendTo = getSendTo();
    const params = { send_to: sendTo, count: 12, page: 1 };
    if (tab !== 'all') params.category = tab;
    ApiClient.get('notification/all', params).then(res => {
      if (res?.success) {
        setNotifications(res.data?.data || []);
      }
      setLoading(false);
    });
  }, [user?.id, getSendTo]);

  // On mount — start count polling & socket listen
  useEffect(() => {
    if (!user?.id) return;
    fetchCategoryCounts();

    const token = localStorage.getItem('token');
    if (token) {
      socketService.connect(token);
      const handleNotification = (data) => {
        // Refresh counts; if dropdown open for the relevant tab, prepend
        fetchCategoryCounts();
        setNotifications(prev => {
          if (activeTab === 'all' || activeTab === data?.category) {
            return [data, ...prev].slice(0, 12);
          }
          return prev;
        });
      };
      socketService.on('notification', handleNotification);
      return () => socketService.off('notification', handleNotification);
    }
  }, [user?.id, fetchCategoryCounts, activeTab]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      const bellEl = bellRef.current;
      const dropEl = dropdownRef.current;
      if (bellEl && !bellEl.contains(e.target) && dropEl && !dropEl.contains(e.target)) {
        setShowDropdown(false);
      } else if (bellEl && !bellEl.contains(e.target) && !dropEl) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!showDropdown) return;
    const reposition = () => computeBellPos();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [showDropdown, computeBellPos]);

  const markAsRead = (id, cat) => {
    ApiClient.put('notification/change-status', { id, status: 'read' }).then(res => {
      if (res?.success) {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, status: 'read' } : n));
        setCounts(prev => {
          const next = { ...prev, total: Math.max(0, prev.total - 1) };
          if (cat && next[cat] !== undefined) next[cat] = Math.max(0, next[cat] - 1);
          return next;
        });
      }
    });
  };

  const handleNotificationClick = (n) => {
    if (n.status === 'unread') markAsRead(n.id, n.category);
    if (n.action_url) {
      setShowDropdown(false);
      history.push(n.action_url);
    }
  };

  const markAllRead = () => {
    ApiClient.put('notification/change-status-all', { status: 'read' }).then(res => {
      if (res?.success) {
        setNotifications(prev => prev.map(n => ({ ...n, status: 'read' })));
        fetchCategoryCounts();
      }
    });
  };

  const getIcon = (n) => {
    if (n.category === 'bids') return 'fa-gavel';
    if (n.category === 'loads') return 'fa-truck';
    if (n.category === 'compliance') return 'fa-shield';
    if (n.category === 'finance') return 'fa-dollar';
    if (n.category === 'account') return 'fa-user-circle';
    if (n.category === 'system') return 'fa-bullhorn';
    switch (n.type) {
      case 'bid': return 'fa-gavel';
      case 'load': return 'fa-truck';
      case 'driver': return 'fa-user';
      case 'truck': return 'fa-truck';
      default: return 'fa-bell';
    }
  };

  const timeAgo = (date) => {
    if (!date) return '';
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const handleTabClick = (key) => {
    setActiveTab(key);
    fetchForTab(key);
  };

  const visibleTabs = TABS.filter(t => t.key === 'all' || (counts[t.key] || 0) > 0 || ['bids', 'loads', 'compliance'].includes(t.key));

  const dropdown = showDropdown ? ReactDOM.createPortal(
    (
      <div
        ref={dropdownRef}
        style={{
          position: 'fixed',
          top: bellPos.top,
          right: bellPos.right,
          width: 380,
          maxHeight: 520,
          background: '#fff',
          borderRadius: 10,
          boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
          zIndex: 100000,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <strong style={{ fontSize: 14 }}>Notifications</strong>
          {counts.total > 0 && (
            <span
              onClick={markAllRead}
              style={{ fontSize: 11, color: '#3E549D', fontWeight: 600, cursor: 'pointer' }}
              title="Mark all as read"
            >
              Mark all read
            </span>
          )}
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          overflowX: 'auto',
          borderBottom: '1px solid #eee',
          background: '#fafafa',
          scrollbarWidth: 'thin',
        }}>
          {visibleTabs.map(tab => {
            const count = tab.key === 'all' ? counts.total : (counts[tab.key] || 0);
            const isActive = activeTab === tab.key;
            return (
              <div
                key={tab.key}
                onClick={() => handleTabClick(tab.key)}
                style={{
                  padding: '8px 10px',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? '#3E549D' : '#666',
                  borderBottom: isActive ? '2px solid #3E549D' : '2px solid transparent',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  transition: 'all 0.15s',
                }}
              >
                <i className={`fa ${tab.icon}`} style={{ fontSize: 11 }}></i>
                <span>{tab.label}</span>
                {count > 0 && (
                  <span style={{
                    background: isActive ? '#3E549D' : '#e5e7eb',
                    color: isActive ? '#fff' : '#666',
                    borderRadius: 10,
                    padding: '1px 6px',
                    fontSize: 10,
                    fontWeight: 700,
                    marginLeft: 2,
                  }}>
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', maxHeight: 370 }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#999' }}>
              <i className="fa fa-spinner fa-spin"></i>
            </div>
          ) : notifications.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#bbb' }}>
              <i className="fa fa-bell-slash" style={{ fontSize: 28, marginBottom: 8 }}></i>
              <p style={{ margin: 0, fontSize: 13 }}>No {activeTab === 'all' ? '' : activeTab} notifications</p>
            </div>
          ) : (
            notifications.map((n, i) => {
              const color = CAT_COLORS[n.category] || '#6b7280';
              const pColor = PRIORITY_COLORS[n.priority] || '#9ca3af';
              return (
                <div
                  key={n.id || i}
                  onClick={() => handleNotificationClick(n)}
                  style={{
                    padding: '10px 14px',
                    borderBottom: '1px solid #f5f5f5',
                    cursor: 'pointer',
                    background: n.status === 'unread' ? '#f8faff' : 'transparent',
                    borderLeft: n.status === 'unread' ? `3px solid ${color}` : '3px solid transparent',
                    transition: 'background 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: 14,
                      background: `${color}15`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <i className={`fa ${getIcon(n)}`} style={{ fontSize: 12, color }}></i>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <div style={{ fontSize: 13, fontWeight: n.status === 'unread' ? 600 : 400, color: '#333', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {n.title || 'Notification'}
                        </div>
                        {n.priority && n.priority !== 'normal' && (
                          <span style={{
                            fontSize: 9,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            color: pColor,
                            background: `${pColor}20`,
                            padding: '1px 5px',
                            borderRadius: 3,
                            letterSpacing: 0.5,
                          }}>
                            {n.priority}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {n.message || ''}
                      </div>
                      <div style={{ fontSize: 10, color: '#aaa', marginTop: 4 }}>
                        {timeAgo(n.createdAt)}
                        {n.sub_type && <span style={{ marginLeft: 8, color: '#ccc' }}>· {n.sub_type}</span>}
                      </div>
                    </div>
                    {n.status === 'unread' && (
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: color, marginTop: 9, flexShrink: 0 }}></div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div
          onClick={() => { setShowDropdown(false); history.push('/notification'); }}
          style={{
            padding: '10px 16px',
            borderTop: '1px solid #eee',
            textAlign: 'center',
            cursor: 'pointer',
            fontSize: 13,
            color: '#3E549D',
            fontWeight: 600,
            background: '#fafafa',
          }}
        >
          View All Notifications
        </div>
      </div>
    ),
    document.body
  ) : null;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div
        ref={bellRef}
        onClick={() => {
          const next = !showDropdown;
          if (next) {
            computeBellPos();
            fetchCategoryCounts();
            fetchForTab(activeTab);
          }
          setShowDropdown(next);
        }}
        style={{ cursor: 'pointer', padding: '8px', position: 'relative' }}
      >
        <i className="fa fa-bell" style={{ fontSize: 20, color: '#555' }}></i>
        {counts.total > 0 && (
          <span style={{
            position: 'absolute',
            top: 2,
            right: 0,
            background: '#e74c3c',
            color: '#fff',
            borderRadius: '50%',
            padding: '1px 5px',
            fontSize: 10,
            fontWeight: 700,
            minWidth: 16,
            textAlign: 'center',
            lineHeight: '14px',
          }}>
            {counts.total > 99 ? '99+' : counts.total}
          </span>
        )}
      </div>
      {dropdown}
    </div>
  );
};

export default NotificationBell;
